import axios from "axios";
import { useCallback, useRef, useState } from "react";
import { useLogin } from "../login";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import { DesignAnalyticsResponse } from "./types";

// #region fetchDesignAnalytics
async function fetchDesignAnalytics(
    designId: string,
    authToken: string,
    startDate?: string,
    endDate?: string
): Promise<DesignAnalyticsResponse | null> {
    const params: string[] = [];
    if (startDate) params.push(`startDate=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`endDate=${encodeURIComponent(endDate)}`);
    const query = params.length ? `?${params.join("&")}` : "";

    const response = await axios.get(
        `${environment.v2Url}/design-analytics/${designId}${query}`,
        { headers: { "X-Wallmates-Auth": authToken } }
    );
    return response.data as DesignAnalyticsResponse;
}

// #region useDesignAnalytics
export const useDesignAnalytics = () => {
    const [, authToken, loginFunctions] = useLogin();
    const [data, setData] = useState<DesignAnalyticsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const reqId = useRef(0);

    // Stabilize references that change every render to prevent infinite useEffect loops
    const authTokenRef = useRef(authToken);
    authTokenRef.current = authToken;
    const logoutRef = useRef(loginFunctions.logout);
    logoutRef.current = loginFunctions.logout;

    const getAnalytics = useCallback(async (
        designId: string,
        startDate?: string,
        endDate?: string
    ) => {
        const myReqId = ++reqId.current;
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const result = await fetchDesignAnalytics(designId, authTokenRef.current || '', startDate, endDate);
            if (myReqId !== reqId.current) return; // superseded
            setData(result);
        } catch (err: any) {
            if (myReqId !== reqId.current) return;
            errorCatcher(err, logoutRef.current);
            setError(err?.response?.data?.message || err.message || "Failed to load analytics");
            setData(null);
        } finally {
            if (myReqId === reqId.current) {
                setLoading(false);
            }
        }
    }, []);

    return { data, loading, error, getAnalytics };
};
